import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import RefreshButton from '../components/RefreshButton';
import SearchableSelect from '../components/SearchableSelect';
import { useDeleteModal } from '../hooks/useDeleteModal';

function Descritores() {
  const { user } = useAuth();
  const { darkMode } = useTheme();
  const isAdmin = user?.role === ROLES.ADMIN;

  // Estados
  const [descriptors, setDescriptors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterField, setFilterField] = useState('');

  // Paginacao
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  // View mode
  const [viewMode, setViewMode] = useState('grid');

  // Modal de detalhes
  const [selectedDescriptor, setSelectedDescriptor] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Modal de criação
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    descriptor_code: '',
    descriptor_name: '',
    subject: '',
    learning_field: '',
  });
  const [createFormErrors, setCreateFormErrors] = useState({});
  const [savingCreate, setSavingCreate] = useState(false);

  // Modal de edição
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    descriptor_code: '',
    descriptor_name: '',
    subject: '',
    learning_field: '',
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editingDescriptor, setEditingDescriptor] = useState(null);

  // Delete modal
  const { isDeleteModalOpen, itemToDelete, openDeleteModal, closeDeleteModal } = useDeleteModal();

  // Buscar descritores
  const fetchDescriptors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        page_size: itemsPerPage
      };
      if (filterSubject) params.subject = filterSubject;
      if (filterGrade) params.grade = filterGrade;
      if (filterField) params.learning_field = filterField;
      if (searchTerm) params.search = searchTerm;

      const response = await apiGet('/descriptors/', params);
      setDescriptors(response.results || []);
      setTotalCount(response.count || 0);
    } catch (err) {
      console.error('Erro ao buscar descritores:', err);
      setError('Erro ao carregar descritores. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterSubject, filterGrade, filterField, searchTerm]);

  useEffect(() => {
    fetchDescriptors();
  }, [fetchDescriptors]);

  // Disciplinas e series disponiveis (com acentos para corresponder ao banco de dados)
  const subjects = ['Matemática', 'Língua Portuguesa'];
  const grades = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  // Campos de aprendizagem unicos extraidos dos dados
  const [availableLearningFields, setAvailableLearningFields] = useState([]);

  // Extrair campos de aprendizagem unicos dos descritores carregados
  useEffect(() => {
    if (descriptors.length > 0) {
      const fields = [...new Set(descriptors.map(d => d.learning_field).filter(Boolean))];
      setAvailableLearningFields(prev => {
        const merged = [...new Set([...prev, ...fields])];
        // `sort()` sem comparador ordena por código UTF-16: "Álgebra" cairia
        // depois de "Zoologia". Em lista de nomes em português o comparador
        // tem de ser o do idioma.
        return merged.sort((a, b) => String(a).localeCompare(String(b), 'pt-BR'));
      });
    }
  }, [descriptors]);

  // Cores por disciplina
  const getSubjectColor = (subject) => {
    const colors = {
      'Matemática': { bg: 'bg-[#0974BA]/20', text: 'text-[#0974BA]', border: 'border-[#0974BA]/50' },
      'Língua Portuguesa': { bg: 'bg-[#2E4974]/20', text: 'text-[#2E4974]', border: 'border-[#2E4974]/50' },
    };
    return colors[subject] || { bg: 'bg-slate-500/20', text: 'text-slate-400', border: 'border-slate-500/50' };
  };

  // Limpar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilterSubject('');
    setFilterGrade('');
    setFilterField('');
    setCurrentPage(1);
  };

  // Abrir modal de detalhes
  const openDescriptorModal = (descriptor) => {
    setSelectedDescriptor(descriptor);
    setShowModal(true);
  };

  // Abrir modal de criação
  const handleOpenCreate = () => {
    setCreateFormData({
      descriptor_code: '',
      descriptor_name: '',
      subject: '',
      learning_field: '',
    });
    setCreateFormErrors({});
    setShowCreateModal(true);
  };

  // Salvar novo descritor
  const handleCreateSave = async () => {
    const errors = {};
    if (!createFormData.descriptor_code.trim()) errors.descriptor_code = 'Código é obrigatório';
    if (!createFormData.descriptor_name.trim()) errors.descriptor_name = 'Nome é obrigatório';
    if (!createFormData.subject) errors.subject = 'Disciplina é obrigatória';
    setCreateFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingCreate(true);
    try {
      await apiPost('/descriptors/', createFormData);
      setShowCreateModal(false);
      fetchDescriptors();
    } catch (err) {
      console.error('Erro ao criar descritor:', err);
      setCreateFormErrors({ submit: err.message || 'Erro ao criar descritor' });
    } finally {
      setSavingCreate(false);
    }
  };

  // Abrir modal de edição
  const handleOpenEdit = (descriptor, e) => {
    if (e) e.stopPropagation();
    setEditingDescriptor(descriptor);
    setEditFormData({
      descriptor_code: descriptor.descriptor_code || '',
      descriptor_name: descriptor.descriptor_name || '',
      subject: descriptor.subject || '',
      learning_field: descriptor.learning_field || '',
    });
    setEditFormErrors({});
    setShowEditModal(true);
  };

  // Salvar edição
  const handleEditSave = async () => {
    const errors = {};
    if (!editFormData.descriptor_code.trim()) errors.descriptor_code = 'Código é obrigatório';
    if (!editFormData.descriptor_name.trim()) errors.descriptor_name = 'Nome é obrigatório';
    if (!editFormData.subject) errors.subject = 'Disciplina é obrigatória';
    setEditFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSavingEdit(true);
    try {
      await apiPut(`/descriptors/${editingDescriptor.id}/`, editFormData);
      setShowEditModal(false);
      fetchDescriptors();
    } catch (err) {
      console.error('Erro ao editar descritor:', err);
      setEditFormErrors({ submit: err.message || 'Erro ao editar descritor' });
    } finally {
      setSavingEdit(false);
    }
  };

  // Excluir descritor
  const handleDeleteClick = (descriptor, e) => {
    if (e) e.stopPropagation();
    openDeleteModal(descriptor);
  };

  const handleDelete = async (descriptor) => {
    await apiDelete(`/descriptors/${descriptor.id}/`);
    fetchDescriptors();
  };

  // Total de paginas
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (loading && descriptors.length === 0) {
    return (
      <LoadingSpinnerAdvanced
        darkMode={darkMode}
        message="Carregando Descritores..."
        // subtitle={userCity ? `Buscando dados de ${userCity.name}...` : 'Buscando informações do banco de dados...'}
        size="xl"
        animationType="spin-pulse"
      />
    );
  }

  return (
    <>
      {/* Header */}
      <header className="flex flex-wrap gap-4 justify-between items-start mb-6 md:mb-8">
        <div>
          <h1 className={`text-2xl md:text-3xl font-medium bg-clip-text text-transparent ${darkMode
            ? 'bg-gradient-to-r from-gray-50 to-gray-400'
            : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974]'
            }`}>
            Catálogo de Descritores
          </h1>
          <p className={`mt-2 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            Explore os descritores pedagógicos por disciplina, série e campo de aprendizagem
          </p>
        </div>
        <div className="flex gap-3">
          <RefreshButton onClick={fetchDescriptors} />
          {isAdmin && (
            <button
              onClick={handleOpenCreate}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25 hover:-translate-y-0.5 transition-all"
            >
              + Novo Descritor
            </button>
          )}
        </div>
      </header>

      {/* Mensagem de erro */}
      {error && (
        <div className="mb-4 p-4 rounded-xl bg-[#E83A3C]/10 border border-[#E83A3C]/20 text-[#E83A3C]">
          {error}
          <button onClick={() => setError(null)} className="float-right font-bold">x</button>
        </div>
      )}

      {/* Estatisticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total de Descritores', value: totalCount, icon: '🎯', color: 'blue' },
          { label: 'Disciplinas', value: subjects.length, icon: '📚', color: 'green' },
          { label: 'Séries', value: '1 ao 9 ano', icon: '🎓', color: 'purple' },
          { label: 'Filtrados', value: descriptors.length, icon: '🔍', color: 'orange' }
        ].map(stat => (
          <div
            key={stat.label}
            className={`rounded-xl p-4 ${darkMode
              ? 'bg-slate-800/50 border border-slate-700/10'
              : 'bg-white/80 border border-slate-200 shadow-sm'
              }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center text-xl ${stat.color === 'blue' ? 'bg-[#0974BA]/20' :
                stat.color === 'green' ? 'bg-[#13983D]/20' :
                  stat.color === 'purple' ? 'bg-[#2E4974]/20' :
                    'bg-[#F9B047]/20'
                }`}>
                {stat.icon}
              </div>
              <div>
                <div className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0974BA] to-[#2E4974]">
                  {stat.value}
                </div>
                <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {stat.label}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className={`rounded-xl p-4 mb-6 ${darkMode
        ? 'bg-slate-800/50 border border-slate-700/10'
        : 'bg-white/80 border border-slate-200 shadow-sm'
        }`}>
        <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
          🔍 Filtros de Busca
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Busca */}
          <div className="md:col-span-2 lg:col-span-4">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">🔍</span>
              <input
                type="text"
                placeholder="Buscar código, nome ou descrição..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className={`w-full pl-10 pr-4 py-2.5 rounded-lg text-sm ${darkMode
                  ? 'bg-slate-700/50 border-slate-600 text-slate-200 placeholder-slate-400'
                  : 'bg-slate-50 border-slate-200 text-slate-700 placeholder-slate-400'
                  } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50`}
              />
            </div>
          </div>

          {/* Disciplina */}
          <SearchableSelect
            value={filterSubject}
            onChange={(v) => { setFilterSubject(v); setFilterField(''); setCurrentPage(1); }}
            options={subjects}
            emptyOptionLabel="Todas disciplinas"
            darkMode={darkMode}
            className="min-w-[180px]"
          />

          {/* Campo de Aprendizagem */}
          <SearchableSelect
            value={filterField}
            onChange={(v) => { setFilterField(v); setCurrentPage(1); }}
            options={availableLearningFields}
            emptyOptionLabel="Todos os campos"
            darkMode={darkMode}
            className="min-w-[180px]"
          />

          {/* Serie */}
          <SearchableSelect
            value={filterGrade}
            onChange={(v) => { setFilterGrade(v); setCurrentPage(1); }}
            options={grades.map(g => ({ value: g, label: `${g} Ano` }))}
            emptyOptionLabel="Todas series"
            darkMode={darkMode}
            className="min-w-[140px]"
          />
        </div>

        <div className="flex justify-end mt-3">
          {/* Limpar */}
          <button
            onClick={clearFilters}
            className={`px-4 py-2.5 rounded-lg text-sm font-medium ${darkMode
              ? 'bg-[#E83A3C]/20 text-[#E83A3C] hover:bg-[#E83A3C]/30'
              : 'bg-[#E83A3C]/10 text-[#E83A3C] hover:bg-[#E83A3C]/20'
              }`}
          >
            Limpar filtros
          </button>
        </div>
      </div>

      {/* Controles de visualizacao */}
      <div className={`flex justify-between items-center mb-4`}>
        <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Exibindo <strong className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{descriptors.length}</strong> de <strong className={darkMode ? 'text-slate-200' : 'text-slate-700'}>{totalCount}</strong> descritores
        </span>
        <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
          <button
            onClick={() => setViewMode('grid')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'grid'
              ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white'
              : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            Grade
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === 'list'
              ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white'
              : darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            Lista
          </button>
        </div>
      </div>

      {/* Grid de Descritores */}
      {descriptors.length === 0 ? (
        <div className={`rounded-xl p-10 text-center ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="text-5xl mb-4">📭</div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
            Nenhum descritor encontrado
          </h3>
          <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Tente ajustar os filtros ou fazer uma nova busca
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {descriptors.map(descriptor => {
            const colors = getSubjectColor(descriptor.subject);
            return (
              <div
                key={descriptor.id}
                onClick={() => openDescriptorModal(descriptor)}
                className={`rounded-xl p-5 cursor-pointer transition-all hover:shadow-lg ${darkMode
                  ? `bg-slate-800/50 border border-slate-700/10 hover:${colors.border}`
                  : 'bg-white/80 border border-slate-200 shadow-sm hover:border-[#0974BA]/50'
                  }`}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <span className={`px-3 py-1 rounded-lg text-sm font-mono font-bold ${colors.bg} ${colors.text}`}>
                    {descriptor.descriptor_code}
                  </span>
                  <div className="flex gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}>
                      {descriptor.subject || 'N/A'}
                    </span>
                    {descriptor.grade && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                        {descriptor.grade} Ano
                      </span>
                    )}
                  </div>
                </div>

                {/* Titulo */}
                <h4 className={`font-semibold mb-2 line-clamp-2 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                  {descriptor.descriptor_name}
                </h4>

                {/* Descrição */}
                <p className={`text-sm line-clamp-3 mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {descriptor.descriptor_description || 'Sem descrição disponível'}
                </p>

                {/* Footer */}
                <div className={`pt-3 border-t flex justify-between items-center ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {descriptor.learning_field || 'Campo não definido'}
                  </span>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <>
                        <button
                          onClick={(e) => handleOpenEdit(descriptor, e)}
                          className={`p-1.5 rounded-lg transition-all ${darkMode
                            ? 'hover:bg-[#0974BA]/20 text-slate-400 hover:text-[#0974BA]'
                            : 'hover:bg-[#0974BA]/10 text-slate-400 hover:text-[#0974BA]'
                            }`}
                          title="Editar descritor"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(descriptor, e)}
                          className={`p-1.5 rounded-lg transition-all ${darkMode
                            ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                            : 'hover:bg-[#E83A3C]/10 text-slate-400 hover:text-[#E83A3C]'
                            }`}
                          title="Excluir descritor"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // View de lista
        <div className={`rounded-xl overflow-hidden mb-6 ${darkMode
          ? 'bg-slate-800/50 border border-slate-700/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
          }`}>
          <div className="divide-y divide-slate-700/20">
            {descriptors.map(descriptor => {
              const colors = getSubjectColor(descriptor.subject);
              return (
                <div
                  key={descriptor.id}
                  onClick={() => openDescriptorModal(descriptor)}
                  className={`flex items-center gap-4 p-4 cursor-pointer transition-all ${darkMode
                    ? 'hover:bg-slate-700/30'
                    : 'hover:bg-slate-50'
                    }`}
                >
                  <span className={`px-3 py-1.5 rounded-lg text-sm font-mono font-bold min-w-[80px] text-center ${colors.bg} ${colors.text}`}>
                    {descriptor.descriptor_code}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-medium truncate ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                      {descriptor.descriptor_name}
                    </h4>
                    <p className={`text-xs truncate ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {descriptor.learning_field || 'Campo não definido'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}>
                      {descriptor.subject || 'N/A'}
                    </span>
                    {descriptor.grade && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                        {descriptor.grade} Ano
                      </span>
                    )}
                    {isAdmin && (
                      <>
                        <button
                          onClick={(e) => handleOpenEdit(descriptor, e)}
                          className={`p-1.5 rounded-lg transition-all ${darkMode
                            ? 'hover:bg-[#0974BA]/20 text-slate-400 hover:text-[#0974BA]'
                            : 'hover:bg-[#0974BA]/10 text-slate-400 hover:text-[#0974BA]'
                            }`}
                          title="Editar descritor"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(descriptor, e)}
                          className={`p-1.5 rounded-lg transition-all ${darkMode
                            ? 'hover:bg-[#E83A3C]/20 text-slate-400 hover:text-[#E83A3C]'
                            : 'hover:bg-[#E83A3C]/10 text-slate-400 hover:text-[#E83A3C]'
                            }`}
                          title="Excluir descritor"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Paginacao */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${currentPage === 1
              ? 'opacity-50 cursor-not-allowed'
              : darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            ←
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${currentPage === pageNum
                  ? 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] text-white'
                  : darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${currentPage === totalPages
              ? 'opacity-50 cursor-not-allowed'
              : darkMode ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
          >
            →
          </button>
        </div>
      )}

      {/* Modal de Detalhes */}
      {showModal && selectedDescriptor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className={`w-full max-w-2xl rounded-2xl ${darkMode
            ? 'bg-slate-800 border border-[#0974BA]/30'
            : 'bg-white shadow-xl'
            }`}>
            <div className={`p-6 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <div className="flex justify-between items-start">
                <div>
                  <span className={`px-3 py-1 rounded-lg text-sm font-mono font-bold ${getSubjectColor(selectedDescriptor.subject).bg} ${getSubjectColor(selectedDescriptor.subject).text}`}>
                    {selectedDescriptor.descriptor_code}
                  </span>
                  <div className="flex gap-2 mt-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${getSubjectColor(selectedDescriptor.subject).bg} ${getSubjectColor(selectedDescriptor.subject).text}`}>
                      {selectedDescriptor.subject || 'N/A'}
                    </span>
                    {selectedDescriptor.grade && (
                      <span className={`px-2 py-0.5 rounded-full text-xs ${darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                        {selectedDescriptor.grade} Ano
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                {selectedDescriptor.descriptor_name}
              </h2>

              <p className={`mb-6 leading-relaxed ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {selectedDescriptor.descriptor_description || 'Sem descrição detalhada disponível para este descritor.'}
              </p>

              {/* Campo de Aprendizagem */}
              {selectedDescriptor.learning_field && (
                <div className={`mb-4 p-4 rounded-xl ${darkMode ? 'bg-[#13983D]/10 border border-[#13983D]/20' : 'bg-[#13983D]/5 border border-[#13983D]/20'}`}>
                  <h5 className={`font-semibold mb-1 ${darkMode ? 'text-[#13983D]' : 'text-[#13983D]'}`}>
                    Campo de Aprendizagem
                  </h5>
                  <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {selectedDescriptor.learning_field}
                  </p>
                </div>
              )}
            </div>

            <div className={`p-6 border-t flex justify-end ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
              <button
                onClick={() => setShowModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Criação */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Novo Descritor
            </h2>

            <div className="space-y-4">
              {/* Código */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Código *
                </label>
                <input
                  type="text"
                  value={createFormData.descriptor_code}
                  onChange={(e) => setCreateFormData({ ...createFormData, descriptor_code: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${createFormErrors.descriptor_code ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: D01"
                />
                {createFormErrors.descriptor_code && (
                  <p className="text-[#E83A3C] text-xs mt-1">{createFormErrors.descriptor_code}</p>
                )}
              </div>

              {/* Nome */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome *
                </label>
                <input
                  type="text"
                  value={createFormData.descriptor_name}
                  onChange={(e) => setCreateFormData({ ...createFormData, descriptor_name: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${createFormErrors.descriptor_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: Resolver problemas com números naturais"
                />
                {createFormErrors.descriptor_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{createFormErrors.descriptor_name}</p>
                )}
              </div>

              {/* Disciplina */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Disciplina *
                </label>
                <SearchableSelect
                  value={createFormData.subject}
                  onChange={(v) => setCreateFormData({ ...createFormData, subject: v, learning_field: '' })}
                  options={subjects}
                  placeholder="Selecione uma disciplina"
                  required
                  darkMode={darkMode}
                  className={`w-full ${createFormErrors.subject ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                />
                {createFormErrors.subject && (
                  <p className="text-[#E83A3C] text-xs mt-1">{createFormErrors.subject}</p>
                )}
              </div>

              {/* Campo de Aprendizagem */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Campo de Aprendizagem
                </label>
                <SearchableSelect
                  value={createFormData.learning_field}
                  onChange={(v) => setCreateFormData({ ...createFormData, learning_field: v })}
                  options={availableLearningFields}
                  placeholder={createFormData.subject ? 'Selecione um campo' : 'Selecione uma disciplina primeiro'}
                  disabled={!createFormData.subject}
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>

              {createFormErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{createFormErrors.submit}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowCreateModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={savingCreate}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateSave}
                disabled={savingCreate}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${savingCreate
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                  }`}
              >
                {savingCreate ? 'Salvando...' : 'Criar Descritor'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de Edição */}
      {showEditModal && editingDescriptor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`w-full max-w-lg rounded-2xl p-6 ${darkMode
            ? 'bg-slate-800 border border-slate-700'
            : 'bg-white shadow-xl'
            }`}>
            <h2 className={`text-xl font-semibold mb-6 ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
              Editar Descritor
            </h2>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Código *
                </label>
                <input
                  type="text"
                  value={editFormData.descriptor_code}
                  onChange={(e) => setEditFormData({ ...editFormData, descriptor_code: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${editFormErrors.descriptor_code ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: D01"
                />
                {editFormErrors.descriptor_code && (
                  <p className="text-[#E83A3C] text-xs mt-1">{editFormErrors.descriptor_code}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Nome *
                </label>
                <input
                  type="text"
                  value={editFormData.descriptor_name}
                  onChange={(e) => setEditFormData({ ...editFormData, descriptor_name: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg text-sm ${darkMode
                    ? 'bg-slate-700/50 border-slate-600 text-slate-200'
                    : 'bg-slate-50 border-slate-200 text-slate-700'
                    } border focus:outline-none focus:ring-2 focus:ring-[#0974BA]/50 ${editFormErrors.descriptor_name ? 'border-[#E83A3C]' : ''}`}
                  placeholder="Ex: Resolver problemas com números naturais"
                />
                {editFormErrors.descriptor_name && (
                  <p className="text-[#E83A3C] text-xs mt-1">{editFormErrors.descriptor_name}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Disciplina *
                </label>
                <SearchableSelect
                  value={editFormData.subject}
                  onChange={(v) => setEditFormData({ ...editFormData, subject: v, learning_field: '' })}
                  options={subjects}
                  placeholder="Selecione uma disciplina"
                  required
                  darkMode={darkMode}
                  className={`w-full ${editFormErrors.subject ? 'ring-2 ring-[#E83A3C] rounded-lg' : ''}`}
                />
                {editFormErrors.subject && (
                  <p className="text-[#E83A3C] text-xs mt-1">{editFormErrors.subject}</p>
                )}
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  Campo de Aprendizagem
                </label>
                <SearchableSelect
                  value={editFormData.learning_field}
                  onChange={(v) => setEditFormData({ ...editFormData, learning_field: v })}
                  options={availableLearningFields}
                  placeholder={editFormData.subject ? 'Selecione um campo' : 'Selecione uma disciplina primeiro'}
                  disabled={!editFormData.subject}
                  darkMode={darkMode}
                  className="w-full"
                />
              </div>

              {editFormErrors.submit && (
                <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#E83A3C]/10 border border-[#E83A3C]/20' : 'bg-[#E83A3C]/5 border border-[#E83A3C]/20'}`}>
                  <p className={`text-sm ${darkMode ? 'text-[#E83A3C]' : 'text-[#E83A3C]'}`}>{editFormErrors.submit}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-700/10">
              <button
                onClick={() => setShowEditModal(false)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${darkMode
                  ? 'bg-slate-700/50 text-slate-300 hover:bg-slate-700'
                  : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                disabled={savingEdit}
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSave}
                disabled={savingEdit}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all ${savingEdit
                  ? 'bg-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-[#0974BA] to-[#2E4974] hover:shadow-lg hover:shadow-[#0974BA]/25'
                  }`}
              >
                {savingEdit ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        item={itemToDelete}
        itemName={itemToDelete?.descriptor_code ? `${itemToDelete.descriptor_code} - ${itemToDelete.descriptor_name}` : ''}
        itemType="Descritor"
        warningMessage="Este descritor será removido permanentemente. Questões vinculadas a este descritor poderão ser afetadas."
      />
    </>
  );
}

export default Descritores;
